import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { Users, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { type FlowParticipant, useShareWorkflow } from "../hooks/use-share-workflow"

type FlowShareDialogProps = {
  workflowId: string
  workflowTitle?: string
  initialParticipants?: FlowParticipant[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FlowShareDialog({
  workflowId,
  workflowTitle,
  initialParticipants = [],
  open,
  onOpenChange,
}: FlowShareDialogProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const share = useShareWorkflow(workflowId)
  const [participants, setParticipants] = useState<FlowParticipant[]>(initialParticipants)

  // The dialog is mounted persistently and toggled via `open`, so without this
  // resync stale edits from a previous session would linger when it reopens.
  // Gate on `open` (the default `initialParticipants = []` is a fresh array each
  // render, so resyncing on identity alone would clobber edits on every render).
  useEffect(() => {
    if (open) setParticipants(initialParticipants)
  }, [open, initialParticipants])

  const addParticipant = (result: DirectorySearchResult) => {
    if (participants.some((p) => p.id === result.oid)) return
    setParticipants((prev) => [
      ...prev,
      { type: result.type, id: result.oid, name: result.displayName, role: "runner" },
    ])
  }

  const setRole = (id: string, role: FlowParticipant["role"]) => {
    setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, role } : p)))
  }

  const removeParticipant = (id: string) => {
    setParticipants((prev) => prev.filter((p) => p.id !== id))
  }

  const handleSave = async () => {
    try {
      await share.mutateAsync(participants)
      toast.success(t("share.saved", "Sharing updated"))
      onOpenChange(false)
    } catch {
      toast.error(t("share.saveFailed", "Could not update sharing"))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="size-4" />
            {t("share.title", "Share flow")}
            {workflowTitle ? ` — ${workflowTitle}` : ""}
          </DialogTitle>
          <DialogDescription>
            {t("share.description", "Share this flow with people or groups so they can run it.")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("share.addPeople", "Add people or groups")}</Label>
            <DirectorySearch
              placeholder={t("share.searchPlaceholder", "Search people or groups…")}
              selectedOids={participants.map((p) => p.id)}
              onSelect={addParticipant}
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs">{t("share.peopleWithAccess", "People with access")}</Label>
            {participants.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                {t("share.noneYet", "Not shared with anyone yet.")}
              </p>
            ) : (
              <ul className="grid gap-2">
                {participants.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-md border p-2 text-xs"
                  >
                    <span className="min-w-0 truncate">{p.name || p.id}</span>
                    <div className="flex items-center gap-1">
                      <Select
                        value={p.role}
                        onValueChange={(v) => setRole(p.id, v as FlowParticipant["role"])}
                      >
                        <SelectTrigger className="h-7 w-[110px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Only "Can run" is offered for now — a shared "editor"
                              role isn't honored yet (shared flows open read-only;
                              there's no backend write path for shared editors), so
                              we don't advertise an edit capability that does nothing. */}
                          <SelectItem value="runner">{t("share.roleRunner", "Can run")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        aria-label={t("share.remove", "Remove")}
                        className="size-7 p-0"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeParticipant(p.id)}
                      >
                        <X className="size-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("share.cancel", "Cancel")}
          </Button>
          <Button disabled={share.isPending} onClick={() => void handleSave()}>
            {t("share.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
